<template>
  <section
    id="overview"
    ref="revealTarget"
    class="section home-overview home-scroll-reveal"
    :class="{ 'is-reveal-ready': isRevealReady, 'is-revealed': isRevealed }"
  >
    <div class="page-container">
      <div class="overview-grid">
        <div class="overview-copy">
          <span class="section-kicker">ABOUT XINGKE</span>
          <h2>扎根兴隆，服务热带特色农业产业</h2>
          <p>
            海南兴科热带作物工程技术有限公司位于海南省万宁市兴隆热带植物园内，是一家集热带特色植物资源科技创新、产品研发、定制生产、技术服务及市场化销售为一体的科技企业。
          </p>
          <p>
            企业依托科研平台和中试基地，将热带作物资源、科研成果与产业需求连接起来，形成从技术到产品、从实验室到生产场景的完整能力链。
          </p>
          <nuxt-link class="section-more" to="/company/profile">
            查看更多
            <i class="i-custom-enter more-link-icon-desktop" aria-hidden="true"></i>
            <i class="i-lucide-chevron-right more-link-icon-mobile" aria-hidden="true"></i>
          </nuxt-link>
        </div>
        <div class="overview-image">
          <home-media
            source="/images/company/strength/research-building.jpg"
            fallback-source="/images/company/campus.webp"
            alt="海南兴科综合实验楼"
          />
        </div>
      </div>

      <div class="data-band" aria-label="企业实力数据">
        <div v-for="metric in metrics" :key="metric.label" class="data-item">
          <span class="data-number">
            <span>{{ metric.number }}</span>
            <small>{{ metric.unit }}</small>
          </span>
          <span class="data-label">{{ metric.label }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  defineOptions({
    name: 'HomeOverview',
  })

  const {
    targetRef: revealTarget,
    isReady: isRevealReady,
    isVisible: isRevealed,
  } = useScrollReveal(0.12)

  const metrics = [
    { label: '热带特色作物产品中试基地', number: '15,000', unit: '+㎡' },
    { label: '中试生产线', number: '8', unit: '条' },
    { label: '授权发明专利', number: '50', unit: '+项' },
    { label: '研发及生产设备', number: '700', unit: '+台套' },
  ]
</script>

<style scoped lang="scss">
  .home-overview {
    background: var(--surface);
  }

  .overview-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.08fr) minmax(0, 0.92fr);
    gap: 72px;
    align-items: start;
  }

  .overview-image {
    height: 365px;
    overflow: hidden;
    border-radius: var(--radius-md);
  }

  .overview-copy {
    min-width: 0;
    padding-top: 10px;
  }

  .overview-copy h2 {
    max-width: 9em;
    margin: 0;
    font-size: 42px;
    font-weight: 620;
    line-height: 1.24;
  }

  .overview-copy p {
    // max-width: 64ch;
    margin: 24px 0 0;
    font-size: 14px;
    line-height: 1.8;
    color: var(--muted);
  }

  .section-more {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    margin: 32px 0 8px;
    font-size: 14px;
    font-weight: 400;
    color: var(--more);
    cursor: pointer;
  }

  .section-more:hover {
    color: var(--accent);
  }

  .section-more i {
    width: 16px;
    height: 16px;
    transition: transform var(--fast) var(--ease);
  }

  .section-more:hover i {
    transform: translateX(4px);
  }

  .data-band {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    margin-top: 52px;
    border-top: 1px solid var(--line);
    border-bottom: 1px solid var(--line);
  }

  .data-item {
    padding: 28px 32px;
    border-right: 1px solid var(--line);
  }

  .data-item:last-child {
    border-right: 0;
  }

  .data-number {
    display: block;
    font: 600 48px/1 var(--num);
    font-variant-numeric: tabular-nums;
    color: var(--accent);
  }

  .data-number small {
    margin-left: 4px;
    font-size: 0.42em;
  }

  .data-label {
    display: block;
    margin-top: 10px;
    font-size: 14px;
    color: var(--muted);
  }

  @media (max-width: 767px) {
    .overview-grid {
      grid-template-columns: minmax(0, 1fr);
      gap: 28px;
    }

    .overview-copy {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      column-gap: 16px;
      align-items: center;
      padding-top: 0;
    }

    .section-kicker {
      grid-row: 1;
      grid-column: 1;
      min-width: 0;
      margin-bottom: 0;
    }

    .overview-copy h2 {
      grid-row: 2;
      grid-column: 1 / -1;
      max-width: none;
      margin-top: 14px;
      font-size: 30px;
      line-height: 1.28;
    }

    .overview-copy p {
      grid-column: 1 / -1;
      margin-top: 18px;
    }

    .section-more {
      grid-row: 1;
      grid-column: 2;
      align-self: center;
      min-height: 44px;
      margin: 0;
      white-space: nowrap;
    }

    .overview-image {
      max-width: 620px;
      height: auto;
      aspect-ratio: 1.45 / 1;
    }

    .data-band {
      grid-template-columns: repeat(2, 1fr);
      margin-top: 36px;
    }

    .data-item {
      padding: 22px 16px;
    }

    .data-number {
      font-size: 34px;
    }

    .data-item:nth-child(2) {
      border-right: 0;
    }

    .data-item:nth-child(-n + 2) {
      border-bottom: 1px solid var(--line);
    }
  }
</style>
